import { useCallback, useMemo, useRef, useState } from 'react'
import type {
  HopTimusPanelMode,
  RightPanelHopTimusValue,
  RightPanelRequest,
  RightPanelValue,
} from './rightPanelContext'

type FoldState = {
  expanded: boolean
  unfoldedModules: ReadonlySet<string>
  hopTimus: HopTimusPanelMode
  hopTimusAfterFold: boolean
}

const FOLDED = { expanded: false, unfoldedModules: new Set<string>() }

const withoutDockedHopTimus = (mode: HopTimusPanelMode): HopTimusPanelMode => (mode === 'docked' ? 'closed' : mode)

const docked = (current: FoldState): FoldState => ({
  ...current,
  ...FOLDED,
  hopTimus: 'docked',
  hopTimusAfterFold: current.expanded,
})

const useRightPanelState = () => {
  const [{ expanded, unfoldedModules, hopTimus: hopTimusMode, hopTimusAfterFold }, setFoldState] = useState<FoldState>({
    ...FOLDED,
    hopTimus: 'closed',
    hopTimusAfterFold: false,
  })
  const [request, setRequest] = useState<RightPanelRequest>(null)
  const [notifications, setNotifications] = useState<Record<string, number>>({})
  const [hopTimusFocusRequest, setHopTimusFocusRequest] = useState(0)
  const requestCounter = useRef(0)

  const toggle = useCallback(
    () =>
      setFoldState(current =>
        current.expanded
          ? { ...current, ...FOLDED }
          : { ...current, expanded: true, hopTimus: withoutDockedHopTimus(current.hopTimus) },
      ),
    [],
  )

  const toggleModule = useCallback((key: string) => {
    setFoldState(current => {
      const next = new Set(current.unfoldedModules)
      if (!next.delete(key)) next.add(key)
      return { ...current, unfoldedModules: next }
    })
  }, [])

  const openModule = useCallback((key: string) => {
    setFoldState(current => ({
      ...current,
      expanded: true,
      unfoldedModules: new Set(current.unfoldedModules).add(key),
      hopTimus: withoutDockedHopTimus(current.hopTimus),
    }))
    requestCounter.current += 1
    setRequest({ key, id: requestCounter.current })
  }, [])

  const setNotification = useCallback((key: string, count: number) => {
    setNotifications(previous => (previous[key] === count ? previous : { ...previous, [key]: count }))
  }, [])

  const toggleHopTimus = useCallback(
    () =>
      setFoldState(current => (current.hopTimus === 'docked' ? { ...current, hopTimus: 'closed' } : docked(current))),
    [],
  )

  const askHopTimus = useCallback(() => {
    setFoldState(current => (current.hopTimus === 'closed' ? docked(current) : current))
    setHopTimusFocusRequest(count => count + 1)
  }, [])

  const closeHopTimus = useCallback(() => setFoldState(current => ({ ...current, hopTimus: 'closed' })), [])

  const detachHopTimus = useCallback(() => setFoldState(current => ({ ...current, hopTimus: 'floating' })), [])

  const dockHopTimus = useCallback(() => setFoldState(docked), [])

  const panel: RightPanelValue = useMemo(
    () => ({ expanded, toggle, openModule, request, unfoldedModules, toggleModule, setNotification }),
    [expanded, toggle, openModule, request, unfoldedModules, toggleModule, setNotification],
  )

  const hopTimus: RightPanelHopTimusValue = useMemo(
    () => ({
      mode: hopTimusMode,
      afterFold: hopTimusAfterFold,
      focusRequest: hopTimusFocusRequest,
      ask: askHopTimus,
      toggle: toggleHopTimus,
      close: closeHopTimus,
      detach: detachHopTimus,
      dock: dockHopTimus,
    }),
    [
      hopTimusMode,
      hopTimusAfterFold,
      hopTimusFocusRequest,
      askHopTimus,
      toggleHopTimus,
      closeHopTimus,
      detachHopTimus,
      dockHopTimus,
    ],
  )

  return { panel, notifications, hopTimus }
}

export default useRightPanelState
